/**
 * Browser-side consent persistence.
 *
 * The consent *rules* live in `@evolveos/focus-core` so they are identical on every host and
 * unit-testable; this only provides storage.
 *
 * A deliberate limitation, stated plainly because it affects how much this control can be
 * relied on: `localStorage` is per-browser-profile and the employee can clear it. That is
 * acceptable for the Phase 1 browser pilot, where clearing it simply means the notice is
 * shown again before monitoring resumes — the failure mode is "asks for consent again",
 * never "monitors without it". A production deployment records acknowledgement server-side
 * against the enrolled device, which is what makes it auditable.
 */

import type { ConsentStore } from '../core/index.js';

const STORAGE_KEY = 'evolveos.workFocus.consent.v1';

export class LocalStorageConsentStore implements ConsentStore {
  readonly location = `localStorage[${STORAGE_KEY}]`;

  private available(): Storage | null {
    try {
      // Accessing localStorage throws outright in some privacy modes.
      const storage = globalThis.localStorage;
      if (storage === undefined) return null;
      const probe = `${STORAGE_KEY}.probe`;
      storage.setItem(probe, '1');
      storage.removeItem(probe);
      return storage;
    } catch {
      return null;
    }
  }

  async read(): Promise<unknown | null> {
    const storage = this.available();
    if (storage === null) return null;
    const raw = storage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    try {
      return JSON.parse(raw) as unknown;
    } catch {
      // A corrupt record must not be treated as a valid acknowledgement. Returning the raw
      // string makes `consentFromJson` reject it with a clear message.
      return { corrupt: raw };
    }
  }

  async write(record: Record<string, unknown>): Promise<void> {
    const storage = this.available();
    if (storage === null) {
      throw new Error(
        'This browser is not allowing local storage, so a monitoring acknowledgement cannot ' +
          'be recorded on this device. Monitoring will not start. Leave private browsing, or ' +
          'allow site data, and try again.',
      );
    }
    storage.setItem(STORAGE_KEY, JSON.stringify(record));
  }

  async clear(): Promise<void> {
    this.available()?.removeItem(STORAGE_KEY);
  }
}
